import classNames from 'classnames';
import { ATTR_NAMES, getAttrFromProduct } from '../../types/attrNames';
import { EnumProductBlockColorTag } from '../../types/cmsEnums';
import {
  CmsGift,
  CmsProduct,
  CriteriaNumberRange,
  GiftListItem,
} from '../../types/cmsProduct';
import { MaterialIconName } from '../../types/materialIconName';
import {
  findAttributeValue,
  resolveMinimumIncomeValue,
} from '../../utils/attributeMeasure';
import { getEffectiveGiftPrice } from '../../utils/helpers/calculateRewardValue';
import { formatData } from '../../utils/helpers/formatDate';
import {
  formatCurrencyAmount,
  formatNumberWithSeparator,
} from '../../utils/helpers/formatters';
import { tr } from '../../utils/translations/tr';
import { CcProductCardTranslations } from './i18n';
import {
  CcBadge,
  CcEarnRate,
  CcEstimateRate,
  CcProductCardFeeIncomeCell,
} from './types';

/**
 * The card's type scale by role; colour is set where a role is used. Fixed font-size tokens, not the
 * typography classes: those grow at desktop and the card is designed at its 430px 3-up width
 * (FR-1.1.3). The prototype's 10/11px take 12px.
 */
export const CC_CARD_TEXT = {
  /** The product name (14px). */
  title: classNames(
    'font-font-weight/bold text-font-size/sm leading-line-height/sm',
  ),
  /** The headline earn rate, and the annual fee and income beneath it (16px). */
  headline: classNames(
    'font-font-weight/extrabold text-font-size/md leading-line-height/md',
  ),
  /** The MoneyHero offer's top gift (13px). */
  gift: classNames(
    'font-font-weight/bold text-font-size/xs-plus leading-line-height/xs-plus',
  ),
  /** A figure under a label, the estimate, a bank gift (12px). */
  value: classNames(
    'font-font-weight/bold text-font-size/xs leading-line-height/xs',
  ),
  /** Zone labels, "EARN RATE"; the prototype's 11px. */
  label: classNames(
    'font-font-weight/medium uppercase tracking-wide text-font-size/xs leading-line-height/xs',
  ),
  /** An offer's eyebrow; the prototype's 11px. */
  eyebrow: classNames(
    'font-font-weight/semibold text-font-size/xs leading-line-height/xs',
  ),
  /** The badge band and the urgency pill; the prototype's 10px. */
  badge: classNames(
    'font-font-weight/bold text-font-size/xs leading-line-height/xs',
  ),
  /** Anything secondary: captions, conditions, top-up prices; the prototype's 10–11px. */
  note: classNames('text-font-size/xs leading-line-height/xs'),
};

/** Up to two decimals, no trailing zeros: 0.73, 1.5, 2. */
export const formatRate = (rate: number): string =>
  String(Number(rate.toFixed(2)));

/** HK$240K, HK$1.25M; a figure that does not round cleanly keeps its digits. */
export function formatCompactAmount(amount: number, currency: string): string {
  if (amount >= 1_000_000 && amount % 10_000 === 0) {
    return `${currency}${amount / 1_000_000}M`;
  }
  if (amount >= 1_000 && amount % 1_000 === 0) {
    return `${currency}${amount / 1_000}K`;
  }
  return formatCurrencyAmount(amount, currency);
}

/** "Up to 10,000 Asia Miles": the gift's own preposition before its title. */
export const giftTitle = (item: GiftListItem): string =>
  [item.gift_title_preposition, item.gift?.gift_title]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

/**
 * The value net of its top-up, as the gifts are ordered and as the "Gift value" sort ranks them
 * (CMS-2002): a shopper is never shown a value they would pay towards (FR-1.5.6), and the top-up
 * shows beneath it. In the market's currency, as every other figure on the card: the gift's own
 * prefix is free text ("HK$", "HKD$", "$" or blank on staging).
 */
export function formatGiftValue(
  item: GiftListItem,
  currency: string,
): string | undefined {
  const value = getEffectiveGiftPrice(item.gift);
  if (value <= 0) return undefined;
  return formatCurrencyAmount(value, currency);
}

/** "Top up HK$200": the gift's own top-up label, or `fallbackLabel` where it has none. */
export function formatTopUp(
  gift: CmsGift | undefined,
  currency: string,
  fallbackLabel: string,
): string | undefined {
  const topUp = gift?.gift_value?.topup_amount;
  if (typeof topUp !== 'number' || topUp <= 0) return undefined;
  const label = gift?.gift_value?.topup_label?.trim() || fallbackLabel;
  return `${label} ${formatCurrencyAmount(topUp, currency)}`;
}

/** "Spend: HK$8,000 within 60 days" — the same parts the PDP offer criteria render. */
export function formatCriterion(
  criterion: CriteriaNumberRange,
): string | undefined {
  const amount = (raw?: string) => {
    if (!raw) return '';
    const number = Number(raw);
    return Number.isFinite(number)
      ? formatNumberWithSeparator({ value: number, thousandsSeparator: ',' })
      : raw;
  };
  const prefix = criterion.prefix ?? '';
  const start = [
    criterion.starting_point_label?.trim(),
    `${prefix}${amount(criterion.value_start)}`,
  ]
    .filter(Boolean)
    .join(' ');
  const end =
    criterion.ending_point_label && criterion.value_end
      ? ` ${criterion.ending_point_label.trim()} ${prefix}${amount(criterion.value_end)}`
      : '';
  const body = `${start}${end}${criterion.suffix ?? ''}`.trim();
  const text = [criterion.label?.trim(), body].filter(Boolean).join(': ');
  return text || undefined;
}

// The card's market; the server renders in UTC, which date-fns 3 cannot format into another zone.
const HK_CALENDAR_DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Hong_Kong',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * An offer's end date as the day it is in Hong Kong. Read in UTC, an offer ending at midnight HKT
 * (16:00Z, as some MoneyHero offers do) would print the day before on the server and disagree with
 * the browser. `Intl` only finds the day; date-fns prints it, with month names of its own, so the
 * server and every browser produce the same string.
 */
export function formatMarketDate(endDate: string, lang: string): string {
  const instant = new Date(endDate);
  // formatToParts throws on an invalid date; formatData's own fallback is the raw string too.
  if (Number.isNaN(instant.getTime())) return endDate;
  const parts = HK_CALENDAR_DAY.formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value;
  return formatData(
    `${part('year')}-${part('month')}-${part('day')}`,
    lang.startsWith('zh') ? 'yyyy年M月d日' : 'd MMM yyyy',
    lang,
  );
}

/** Our choice: the trending band takes the prototype's colour, the editor's palette is theirs alone. */
const TRENDING_BADGE_COLOR = 'gold';
// The product owner's icons (CMS-2000): an editor badge without an icon of its own, and the trending rank.
const EDITOR_BADGE_ICON: MaterialIconName = 'auto_awesome';
const TRENDING_BADGE_ICON: MaterialIconName = 'diamond';

/** The editor's badge (label, colour and icon from ProductMetaConfig) wins over the trending rank (FR-1.2.5). */
export function resolveBadge(
  product: CmsProduct,
  editor: {
    label: string | undefined;
    color: EnumProductBlockColorTag | undefined;
    icon: MaterialIconName | undefined;
  },
  t: CcProductCardTranslations,
): CcBadge | undefined {
  const label = editor.label?.trim();
  if (label) {
    return {
      label,
      color: editor.color?.toLowerCase(),
      icon: editor.icon ?? EDITOR_BADGE_ICON,
    };
  }

  const rank = product.trending_rank;
  if (rank && rank > 0) {
    return {
      label: tr('cc_card_trending_rank', t, { rank: String(rank) }),
      color: TRENDING_BADGE_COLOR,
      icon: TRENDING_BADGE_ICON,
    };
  }
  return undefined;
}

// The scopes a headline rate is the best of (FR-1.4.1): local, overseas and online spend.
const CASHBACK_RATES = [
  ATTR_NAMES.CASHBACK_LOCAL,
  ATTR_NAMES.CASHBACK_OVERSEAS,
  ATTR_NAMES.CASHBACK_LOCAL_ONLINE_SHOPPING,
  ATTR_NAMES.CASHBACK_OVERSEAS_ONLINE_SHOPPING,
];
const MILES_RATES = [
  ATTR_NAMES.AIRMILES_LOCAL,
  ATTR_NAMES.AIRMILES_OVERSEAS,
  ATTR_NAMES.AIRMILES_LOCAL_ONLINE_SHOPPING,
  ATTR_NAMES.AIRMILES_OVERSEAS_ONLINE_SHOPPING,
];
/** The reward-type tag that makes a card a miles card (the Sort and Filter "Miles" option). */
const MILES_CATEGORY = 'category:airmiles';

/** An authored rate, or `undefined` where it is blank or not positive. */
const readRate = (product: CmsProduct, name: string): number | undefined => {
  const rate = findAttributeValue(product.attributes, name);
  return typeof rate === 'number' && rate > 0 ? rate : undefined;
};

export function resolveEarnRate(product: CmsProduct): CcEarnRate | undefined {
  const readRates = (names: string[]) =>
    names
      .map((name) => readRate(product, name))
      .filter((rate): rate is number => rate !== undefined);

  const percents = readRates(CASHBACK_RATES);
  const dollarsPerMile = readRates(MILES_RATES);
  const cashback: CcEarnRate | undefined = percents.length
    ? { kind: 'cashback', percent: Math.max(...percents) }
    : undefined;
  const miles: CcEarnRate | undefined = dollarsPerMile.length
    ? { kind: 'miles', dollarsPerMile: Math.min(...dollarsPerMile) }
    : undefined;

  // A miles card often states a cashback rate too; its reward type decides which one leads.
  if (miles && product.category_identifiers?.includes(MILES_CATEGORY)) {
    return miles;
  }
  return cashback ?? miles;
}

/**
 * The cap's period is authored only in its suffix ("/ month", "/ quarter"). Our reading: only a
 * monthly cap bounds a monthly figure; a cap over any other period is left out.
 */
const MONTHLY_CAP_SUFFIX = /\bmonth(ly)?\b|每月|\/\s*月/i;

/**
 * Everyday, mostly local spend: the local rate of the headline's kind, not the headline's best
 * scope (FR-1.6.2, CMS-2000). Without it, no estimate (FR-1.6.8).
 */
export function resolveEstimateRate(
  product: CmsProduct,
  earnRate: CcEarnRate | undefined,
): CcEstimateRate | undefined {
  if (!earnRate) return undefined;

  if (earnRate.kind === 'miles') {
    const dollarsPerMile = readRate(product, ATTR_NAMES.AIRMILES_LOCAL);
    return dollarsPerMile ? { kind: 'miles', dollarsPerMile } : undefined;
  }

  const percent = readRate(product, ATTR_NAMES.CASHBACK_LOCAL);
  if (!percent) return undefined;
  const cap = readRate(product, ATTR_NAMES.CASHBACK_BONUS_CAP);
  const capPeriod =
    getAttrFromProduct(ATTR_NAMES.CASHBACK_BONUS_CAP, product)?.suffix ?? '';
  return {
    kind: 'cashback',
    percent,
    monthlyCap: MONTHLY_CAP_SUFFIX.test(capPeriod) ? cap : undefined,
  };
}

// Waivers are authored as text ("First year", "First 2 years", "Perpetual annual fee waiver").
const FIRST_YEAR_WAIVER = /first year|1st year|one year|首年/i;
// A waiver text that already says it is one ("…waiver", "永久免年費"); otherwise it is only the
// period ("First 5 years", "首2年") and the caption names the waiver itself.
const SAYS_WAIVED = /waive|豁免|免/i;

/**
 * The figure the "Lowest annual fee" sort ranks on, so the card never reads cheaper than its place
 * in the list: only a zero fee is free (FR-1.4.6); a waiver, perpetual or not, is a caption.
 */
export function resolveAnnualFee(
  product: CmsProduct,
  t: CcProductCardTranslations,
  currency: string,
): CcProductCardFeeIncomeCell | undefined {
  const label = t.cc_card_annual_fee;
  const amount = findAttributeValue(product.attributes, ATTR_NAMES.ANNUAL_FEE);
  if (amount === 0) {
    return { label, value: t.cc_card_annual_fee_free, isFree: true };
  }
  if (typeof amount !== 'number') {
    // No figure, so the sort leaves it unranked; the attribute's own text says why.
    const text = getAttrFromProduct(
      ATTR_NAMES.ANNUAL_FEE,
      product,
    )?.empty_value_text?.trim();
    return text ? { label, value: text } : undefined;
  }

  const waiver = getAttrFromProduct(ATTR_NAMES.ANNUAL_FEE_WAIVER, product);
  const waiverText = waiver?.value
    ? (waiver.true_value?.trim() ?? '')
    : undefined;
  let caption: string | undefined;
  if (waiverText !== undefined) {
    if (!waiverText || FIRST_YEAR_WAIVER.test(waiverText)) {
      caption = t.cc_card_first_year_waived;
    } else {
      caption = SAYS_WAIVED.test(waiverText)
        ? waiverText
        : tr('cc_card_fee_waived', t, { period: waiverText });
    }
  }
  return { label, value: formatCurrencyAmount(amount, currency), caption };
}

export function resolveMinIncome(
  product: CmsProduct,
  t: CcProductCardTranslations,
  currency: string,
): CcProductCardFeeIncomeCell | undefined {
  const label = t.cc_card_min_income;
  // Same door as the "Lowest minimum income" sort: an absent or blank figure is no requirement.
  const amount = resolveMinimumIncomeValue(
    product.attributes,
    ATTR_NAMES.MINIMUM_ANNUAL_INCOME,
  );
  if (typeof amount !== 'number') return undefined;
  // Never "HK$0" (FR-1.4.7).
  if (amount <= 0) return { label, value: t.cc_card_min_income_none };
  return {
    label,
    value: formatCompactAmount(amount, currency),
    caption: t.cc_card_per_year,
  };
}
